import { z } from 'zod'
import { FieldType, LocaleCode, ParticipantKind } from './enums.js'
import { FieldManifestV1 } from './manifest.js'
import { PackageManifest } from './entities.js'
import { HASH_STRING_PATTERN } from './version.js'

const IsoDateTime = z.string().datetime()

/**
 * Device-to-device envelope package — PRD §7.1, §12.4, REC-02/REC-12, SEC-04.
 *
 * The service only ever stores ciphertext: the sender builds this package on its own device,
 * encrypts it with a fresh envelope key, and the recipient decrypts it locally with the key
 * carried in the capability link's fragment (browsers never send fragments to a server). The
 * package embeds the exact `FieldManifestV1` and `PackageManifest` the create call declared
 * digests for, so the recipient can recompute those digests and refuse a package the service
 * never authorised.
 *
 * Nothing here is validated by the service; it is the shape both ends must agree on.
 */

export const ENVELOPE_PACKAGE_SCHEMA = 'signbento-envelope-package-v1'

export const EnvelopePackageParticipant = z
  .object({
    /** Index into the create request's `participants` array — the server's `participant_index`. */
    participant_index: z.number().int().nonnegative(),
    role_id: z.string().min(1).max(64).optional(),
    kind: ParticipantKind,
    name: z.string().min(1).max(200),
    is_sender: z.boolean(),
    order_index: z.number().int().nonnegative(),
  })
  .strict()
export type EnvelopePackageParticipant = z.infer<typeof EnvelopePackageParticipant>

export const EnvelopePackage = z
  .object({
    schema: z.literal(ENVELOPE_PACKAGE_SCHEMA),
    envelope_id: z.string().min(1).max(128),
    document_title: z.string().min(1).max(300),
    page_count: z.number().int().positive(),
    /** The declared manifest digests the accepted response binds (REC-12). */
    source_manifest: PackageManifest,
    field_manifest: FieldManifestV1,
    /** Base64 of the unencrypted PDF; only ever present inside the sealed package. */
    source_base64: z.string().min(1),
    participants: z.array(EnvelopePackageParticipant).min(1).max(20),
  })
  .strict()
export type EnvelopePackage = z.infer<typeof EnvelopePackage>

/** A manifest digest in the bare-hex form `jcsDigest` produces. */
export const ManifestDigest = z.string().regex(HASH_STRING_PATTERN)
export type ManifestDigest = z.infer<typeof ManifestDigest>

export const RESPONSE_PACKAGE_SCHEMA = 'signbento-response-package-v1'

export const ResponsePackageValue = z
  .object({
    field_id: z.string().min(1).max(64),
    type: FieldType,
    /** Typed value; a signature also carries the drawn artifact. */
    value: z.union([z.string(), z.boolean()]),
    signature_png_base64: z.string().min(1).optional(),
  })
  .strict()
export type ResponsePackageValue = z.infer<typeof ResponsePackageValue>

export const ResponsePackage = z
  .object({
    schema: z.literal(RESPONSE_PACKAGE_SCHEMA),
    envelope_id: z.string().min(1).max(128),
    recipient_id: z.string().min(1).max(128),
    participant_index: z.number().int().nonnegative(),
    capability_version: z.number().int().positive(),
    submitted_at: IsoDateTime,
    /** Binds the response to the exact manifest it answered (REC-12). */
    field_manifest_digest: ManifestDigest,
    values: z.array(ResponsePackageValue).max(500),
    /**
     * The language the signer read the page in. It only decides how Date signed is printed on the
     * final PDF, so the copy shows the date as the signer saw it; the stored value stays `YYYY-MM-DD`.
     * Absent from responses made before it existed (printed in English then).
     */
    locale: LocaleCode.optional(),
  })
  .strict()
export type ResponsePackage = z.infer<typeof ResponsePackage>
